import { TranslateService } from "@ngx-translate/core";
import { SiilaTranslationService } from "../core/services/siila-translation.service";
import { Injector } from "@angular/core";
import { LOCATION_INITIALIZED } from "@angular/common";
import { take } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { findLegacyRedirect } from "../core/config/legacy-redirects";
import { PrerenderReadyService } from "../shared/services/prerender-ready.service";
import { UrlCanonicalService } from "../shared/services/url-canonical.service";
import {
  emitPrerenderRedirect,
  isPrerenderAgent
} from "../shared/utils/prerender";

export function appInitializerFactory(
  translateService: TranslateService,
  siilaTranslationService: SiilaTranslationService,
  injector: Injector
): () => Promise<unknown> {
  return () => {
    // Both URL rewrites run SYNCHRONOUSLY, right here, not in the
    // `LOCATION_INITIALIZED` callback below: the router's own initializer is
    // chained on that same promise and registered first, so by the time a
    // callback here runs it has already read the URL for its initial navigation.
    //
    // Legacy redirects first: a legacy address must not get its language
    // resolved or its route rendered, and the `/lang` cleanup must not rewrite
    // it out from under the table (`/marketanalytics/lang` is a legacy rule on
    // BR). The promise is left pending on purpose — the page is being replaced,
    // so bootstrapping the old URL would only flash its content first.
    if (followLegacyRedirect(siilaTranslationService, injector)) {
      return new Promise<unknown>(() => {});
    }
    // Same page, cleaner URL: no reload needed.
    normalizeLangUrl(siilaTranslationService, injector);

    return new Promise<unknown>((resolve: (value: unknown) => void) => {
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );
      locationInitialized.then(() => {
        translateService.addLangs(["pt", "en", "es"]);
        translateService.setDefaultLang("en");
        const url = window.location.pathname;
        // What the URL asks for: the `/lang/<code>` suffix, or — on news-detail
        // routes — the slug WORD, which outranks the suffix (`/news/...` is the
        // English article, `/noticias/...` the pt/es one). Resolving it HERE, at
        // boot, is what makes the article render in the right language on the
        // first paint instead of flashing the deployment default and correcting
        // itself afterwards.
        const urlLang = siilaTranslationService.getLanguageFromPath(url);
        const resolvedLang =
          urlLang || siilaTranslationService.getDefaultLanguage();
        if (urlLang || !validateUrlLang(url)) {
          try {
            localStorage.setItem("currentLanguageHP", resolvedLang);
          } catch (error) {}
          translateService.setDefaultLang(resolvedLang);
        }

        const language = siilaTranslationService.getCurrentLanguage();
        // Some routes own their address bar and must see the URL as REQUESTED
        // to decide whether it earns a 301: `DetailNewsComponent` rewrites word
        // + slug + suffix once the article resolves, and `SiilaAcademyComponent`
        // classifies the `/lang/<segment>` it arrived on. Normalizing the
        // `/lang` suffix here would make an off-canonical URL look canonical and
        // silently swallow the redirect — which is exactly what happened to
        // `/academy/lang/fibra-analytics`. See `componentOwnsAddressBar`.
        siilaTranslationService
          .setLanguage(
            language,
            siilaTranslationService.componentOwnsAddressBar(url)
          )
          .pipe(take(1))
          .subscribe(
            () => resolve(null),
            err => console.error(err),
            () => resolve(null)
          );
      });
    });
  };
}

/**
 * Send the visitor on if the requested URL is in `LEGACY_REDIRECTS`. Returns
 * true when a redirect was issued.
 *
 * Mirrors `DetailNewsComponent.canonicalizeURL`: the Prerender.io 301 pair is
 * emitted unconditionally (the renderer forwards the crawler's user agent, so it
 * cannot be told apart from a person), and the hard navigation is skipped only
 * when we can tell we are being prerendered, since it would discard the tags.
 * In that case the snapshot is the bare shell carrying the 301, signalled ready
 * right away instead of waiting out Prerender.io's own timeout.
 */
function followLegacyRedirect(
  siilaTranslationService: SiilaTranslationService,
  injector: Injector
): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const rule = findLegacyRedirect(
    environment.platformId,
    window.location.pathname
  );
  if (!rule) {
    return false;
  }
  const base = rule.toPlatform
    ? siilaTranslationService.getPlatformBaseUrl(rule.toPlatform)
    : window.location.origin;
  // Some dev/test environments do not declare the other deployments' URLs.
  if (!base) {
    return false;
  }
  const targetUrl = `${base.replace(/\/+$/, "")}${rule.to}${window.location
    .search || ""}`;

  emitPrerenderRedirect(document, encodeURI(targetUrl));
  if (isPrerenderAgent()) {
    const prerenderReady = injector.get(PrerenderReadyService, null);
    if (prerenderReady) {
      prerenderReady.signalReady();
    }
  } else {
    window.location.replace(targetUrl);
  }
  return true;
}

/**
 * 301 a URL whose `/lang` part is junk — `/press/lang`, `/press/lang/lang/es`,
 * `/products/103/investor-survey/lang` — to its clean form (SIIL-6356). See
 * `SiilaTranslationService.getLangUrlRedirect` for the rules.
 *
 * Unlike `followLegacyRedirect` the target is this same deployment and the same
 * page, so the address bar is corrected in place before the router's initial
 * navigation reads it. Goes through `UrlCanonicalService.apply` rather than
 * emitting the tags by hand: it remembers the URL it redirected to, so a
 * product or solution page canonicalizing onto that same URL later keeps the
 * 301 instead of clearing it as a stale one.
 */
function normalizeLangUrl(
  siilaTranslationService: SiilaTranslationService,
  injector: Injector
): void {
  if (typeof window === "undefined") {
    return;
  }
  const encodedTarget = siilaTranslationService.getLangUrlRedirect(
    window.location.pathname
  );
  if (!encodedTarget) {
    return;
  }
  // `pathname` is percent-encoded (`/products/102/%C3%ADndices`), but every
  // `UrlCanonicalService.apply` caller passes the path RAW (`índices`): apply
  // encodes it once for the `Location` header, and the product page's own
  // canonical path must compare equal to the one remembered here, or it clears
  // this 301 as a stale one.
  let target = encodedTarget;
  try {
    target = decodeURIComponent(encodedTarget);
  } catch (e) {}
  const urlCanonical = injector.get(UrlCanonicalService, null);
  if (urlCanonical) {
    urlCanonical.apply(target);
    return;
  }
  emitPrerenderRedirect(
    document,
    encodeURI(`${window.location.origin}${target}${window.location.search}`)
  );
  siilaTranslationService.replacePathKeepingQuery(target);
}

export function validateUrlLang(url) {
  if (
    url.indexOf("lang/en") >= 0 ||
    url.indexOf("lang/es") >= 0 ||
    url.indexOf("lang/pt-br") >= 0
  ) {
    return true;
  }
  return false;
}
